import { Component, Input, HostListener, OnDestroy } from '@angular/core';
import { Store, select } from '@ngrx/store';
import {
  Observable,
  Subject,
  Subscription,
  combineLatest,
  BehaviorSubject
} from 'rxjs';
import { filter, map, distinctUntilChanged } from 'rxjs/operators';
import { ColDef, GridReadyEvent, IDatasource } from 'ag-grid-community';
import {
  InfiniteScrollingService,
  ComparatorMap,
  dateComparer,
  numberComparer,
  stringComparer,
  ReusableStreamProperty
} from '@ipreo/muni-shared';
import { Tranche } from '../../../gameday/data/gameDay-data.transformations';
import { GameDayRootReducerState } from '../../../state/root.state';
import { getGamedayDataState } from '../../../state/data/reducer';
import { GamedayState } from '../../../state/data/state';
import { TrancheOrder } from '../../models/tranche-order';
import { emptyMessageMapper } from '../../mappers/empty-message.mapper';
import {
  trancheOrdersMapper,
  isTrancheOrdersFilteredMapper
} from '../../mappers/tranche-orders.mapper';
import { CategoryColorComponent } from './category-color/category-color.component';
import { AccountCellRendererComponent } from '../../../shared/components/renderers/account-cell.component';
import { OrderNumberComponent } from '../../../shared/components/order-number/order-number.component';
import { amountFormat } from '../../../gameday/gameday.constants';

@Component({
  selector: 'muni-gameday-tranche-orders-table',
  templateUrl: './tranche-orders-table.template.html',
  styleUrls: ['./tranche-orders-table.styles.scss'],
  standalone: false,
})
export class TrancheOrdersTableComponent implements OnDestroy {
  public readonly columnDefs: ColDef[] = [
    {
      field: 'categoryColumn',
      headerName: '',
      cellRenderer: CategoryColorComponent,
      width: 30,
      minWidth: 30,
      maxWidth: 30,
      suppressHeaderMenuButton: true,
      sortable: false
    },
    {
      field: 'orderNumber',
      headerName: 'Ord #',
      comparator: numberComparer,
      width: 70,
      minWidth: 70,
      maxWidth: 70,
      suppressHeaderMenuButton: true,
      cellRenderer: OrderNumberComponent
    },
    {
      field: 'manager',
      headerName: 'Manager',
      comparator: stringComparer,
      suppressHeaderMenuButton: true
    },
    {
      field: 'series',
      headerName: 'Series',
      comparator: stringComparer,
      width: 95,
      minWidth: 95,
      maxWidth: 95,
      suppressHeaderMenuButton: true
    },
    {
      field: 'maturity',
      headerName: 'Maturity',
      comparator: dateComparer,
      width: 115,
      minWidth: 115,
      maxWidth: 115,
      suppressHeaderMenuButton: true
    },
    {
      field: 'orderType',
      headerName: 'Priority',
      comparator: stringComparer,
      width: 115,
      minWidth: 115,
      maxWidth: 115,
      suppressHeaderMenuButton: true
    },
    {
      field: 'priority',
      headerName: 'Type',
      comparator: stringComparer,
      suppressHeaderMenuButton: true
    },
    {
      field: 'account',
      headerName: 'Account',
      comparator: stringComparer,
      suppressHeaderMenuButton: true,
      cellRenderer: AccountCellRendererComponent
    },
    // {
    //   field: 'zipCode',
    //   headerName: 'Zip Code',
    //   comparator: stringComparer,
    //   width: 85,
    //   minWidth: 85,
    //   maxWidth: 85,
    //   suppressHeaderMenuButton: true
    // },
    {
      field: 'amount',
      headerName: 'Amt ($M)',
      comparator: numberComparer,
      valueFormatter: ({ value }) => amountFormat.format(value),
      width: 95,
      minWidth: 95,
      maxWidth: 95,
      type: 'numericColumn',
      suppressHeaderMenuButton: true
    }
  ];
  private readonly gridReadySubj = new Subject<GridReadyEvent>();
  private readonly resizeSubj = new Subject<Event>();
  private readonly tranche$ = new BehaviorSubject<Tranche>(undefined);

  public readonly sortingOrder: string[] = ['desc', 'asc', null];

  @Input()
  public set tranche(tranche: Tranche) {
    this.tranche$.next(tranche);
  }

  @ReusableStreamProperty
  private readonly orders: Observable<TrancheOrder[]> = this.tranche$.pipe(
    filter((tranche) => !!tranche),
    distinctUntilChanged(),
    map(trancheOrdersMapper)
  );

  @ReusableStreamProperty
  private readonly isTrancheOrdersFilteredMapper: Observable<boolean> =
    this.store.pipe(
      select(getGamedayDataState),
      filter(({ deal }) => !!deal),
      map(
        // ToDo: Fix mapping inside later
        ({
          deal,
          filters: {
            seriesId,
            ignoreStocks,
            isSealedBid,
            searchByAccountManager
          }
        }: GamedayState) => ({
          deal,
          seriesId,
          searchByAccountManager,
          ignoreStocks,
          isSealedBid
        })
      ),
      map(isTrancheOrdersFilteredMapper),
      distinctUntilChanged()
    );

  @ReusableStreamProperty
  public readonly emptyMessage: Observable<string> = combineLatest(
    this.orders,
    this.isTrancheOrdersFilteredMapper
  ).pipe(map(([orders, isFiltered]) => emptyMessageMapper(orders, isFiltered)));

  @ReusableStreamProperty
  private readonly grid$: Observable<[GridReadyEvent, TrancheOrder[]]> =
    combineLatest(this.gridReadySubj, this.orders);

  @ReusableStreamProperty
  private readonly resize$: Observable<[Event, GridReadyEvent]> = combineLatest(
    this.resizeSubj,
    this.gridReadySubj
  );

  private readonly gridSubscription: Subscription = this.grid$.subscribe(
    ([readyEvent, trancheOrders]) => {
      const sortMap: ComparatorMap[] = this.columnDefs
        .map(({ field, comparator }: ColDef) => ({
          colId: field,
          comparator: comparator
            ? (a: any, b: any) => (comparator as any)(a, b)
            : undefined
        }))
        .filter((item) => item.comparator);
      const datasource: IDatasource =
        this.infiniteScrollingService.getDataSource(
          trancheOrders,
          undefined,
          sortMap
        );

      readyEvent.api.setDatasource(datasource);
      readyEvent.api.applyColumnState?.({
        state: [{ colId: 'amountFormatted', sort: 'desc' }] as any,
        applyOrder: true
      });
      readyEvent.api.sizeColumnsToFit?.();
    }
  );

  private readonly resizeSubscription: Subscription = this.resize$.subscribe(
    ([, readyEvent]) => readyEvent.api.sizeColumnsToFit?.()
  );

  constructor(
    private store: Store<GameDayRootReducerState>,
    private infiniteScrollingService: InfiniteScrollingService
  ) {}

  @HostListener('window:resize', ['$event'])
  public onWindowResize(event: Event): void {
    this.resizeSubj.next(event);
  }

  public onGridReady(params: GridReadyEvent): void {
    this.gridReadySubj.next(params);
  }

  ngOnDestroy() {
    this.gridSubscription.unsubscribe();
    this.resizeSubscription.unsubscribe();
  }
}
